import { useIntersectionObserver } from "@vueuse/core";
import { ref, unref } from "vue";

/** Fraction of the visible width one arrow click travels. */
const SCROLL_FRACTION = 0.5;

/**
 * Slack added to each side of the observed box. A row of fractional widths leaves a sentinel a
 * sub-pixel beyond the scroller at its furthest scroll, which a threshold of 1 reads as out of
 * view and reports as more to scroll to.
 */
const EDGE_SLACK = "2px";

/**
 * Reports which side of the bar has chips out of view, and scrolls towards either one.
 *
 * The answer comes from two zero-width sentinels at the ends of the scrolled row rather than from
 * `scrollLeft` arithmetic, so it stays right while the row is still laying out and while a chip is
 * reordering.
 *
 * @param {import("vue").Ref<HTMLElement | null | undefined>} container - the scrolling element.
 * @param {import("vue").Ref<HTMLElement | null | undefined>} start - the sentinel before the chips.
 * @param {import("vue").Ref<HTMLElement | null | undefined>} end - the sentinel after them.
 * @returns {{
 *   hasOverflowStart: import("vue").Ref<boolean>,
 *   hasOverflowEnd: import("vue").Ref<boolean>,
 *   scrollTowardsStart: () => void,
 *   scrollTowardsEnd: () => void,
 * }}
 */
export function useFinderFilterOverflow(container, start, end) {
  const hasOverflowStart = ref(false);
  const hasOverflowEnd = ref(false);

  observe(start, hasOverflowStart);
  observe(end, hasOverflowEnd);

  /**
   * @param {import("vue").Ref<HTMLElement | null | undefined>} sentinel
   * @param {import("vue").Ref<boolean>} hidden
   */
  function observe(sentinel, hidden) {
    useIntersectionObserver(
      sentinel,
      ([entry]) => {
        hidden.value = !entry.isIntersecting;
      },
      { root: container, rootMargin: `0px ${EDGE_SLACK}`, threshold: 1 },
    );
  }

  /**
   * Travels one step towards an inline edge. `scrollBy` takes a physical delta, and a
   * right-to-left row grows towards negative `scrollLeft`, so the sign follows the row's own
   * direction rather than the edge it is heading for.
   *
   * @param {-1 | 1} towards - -1 for the inline start, 1 for the inline end.
   */
  function scrollBy(towards) {
    const element = unref(container);

    if (!element) {
      return;
    }

    const physical = getComputedStyle(element).direction === "rtl" ? -towards : towards;

    element.scrollBy({ left: element.clientWidth * SCROLL_FRACTION * physical, behavior: "smooth" });
  }

  return {
    hasOverflowStart,
    hasOverflowEnd,
    scrollTowardsStart: () => scrollBy(-1),
    scrollTowardsEnd: () => scrollBy(1),
  };
}
